import { useRef, ChangeEvent, KeyboardEvent, ClipboardEvent } from "react";
import { useNavigate } from "react-router-dom";
interface PaymentPinProps {
    code?: string[];
    onCodeChange: (code: string[]) => void;
    forgot?: boolean;
}

export default function PaymentPin({
    code = new Array(4).fill(""),
    onCodeChange,
    forgot = true,
}: PaymentPinProps) {
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
    const navigate = useNavigate();

    const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        if (value.length === 1) {
            const updatedCode = [...code];
            updatedCode[index] = value;
            onCodeChange(updatedCode);

            const nextId = event.target.getAttribute("data-focus-input-next");
            if (nextId) {
                document.getElementById(nextId)?.focus();
            }
        }
    };

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const pasteData = event.clipboardData.getData("text");
        const digits = pasteData.replace(/\D/g, "");

        if (digits.length > 0) {
            const updatedCode = [...code];
            digits.split("").forEach((digit, i) => {
                if (i < updatedCode.length) {
                    updatedCode[i] = digit;
                }
            });

            onCodeChange(updatedCode);
            const lastIndex = Math.min(digits.length, code.length) - 1;
            inputsRef.current[lastIndex]?.focus();
        }
    };

    const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
        const { key } = event;

        if (key === "Backspace") {
            const updatedCode = [...code];
            if (!code[index]) {

                const prevId = event.currentTarget.dataset.focusInputPrev;
                if (prevId) {
                    document.getElementById(prevId)?.focus();
                }
            } else {
                updatedCode[index] = "";
                onCodeChange(updatedCode);
            }
        }
    };

    const handleForgotPin = () => {
        navigate("/forgot-pin", { replace: false, state: { autoSendOtp: true } });
    };

    return (
        <div className="flex flex-col items-center w-full max-w-xs mx-auto font-Manrope">
            {/* Pin inputs */}
            <div className="flex justify-between w-full mb-2 space-x-2">
                {Array.from({ length: 4 }, (_, index) => {
                    const currentId = `code-${index + 1}`;
                    const nextId = index < 3 ? `code-${index + 2}` : null;
                    const prevId = index > 0 ? `code-${index}` : null;

                    return (
                        <div key={currentId}>
                            <label htmlFor={currentId} className="sr-only">
                                Code {index + 1}
                            </label>
                            <input
                                ref={(el: HTMLInputElement | null) => { if (el) inputsRef.current[index] = el; }}
                                id={currentId}

                                data-focus-input-prev={prevId}

                                data-focus-input-next={nextId}
                                className="block w-14 h-14 py-3 text-sm font-medium text-center border-2 border-[#EAEBF0] rounded-[10px] bg-[#F1F1F1] focus:outline-none text-black focus:ring-2 focus:border-transparent outline-none"
                                type="text"
                                maxLength={1}
                                value={code[index] || ""}
                                onChange={(e) => handleChange(index, e)}
                                onPaste={handlePaste}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                required
                            />
                        </div>
                    );
                })}
            </div>

            {/* Forgot pin button */}
            {forgot && (
                <button
                    onClick={handleForgotPin}
                    className="flex items-center justify-center text-[#12121266] font-medium text-xs mt-2 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#317333]"
                >
                    Forgot Payment Pin?
                </button>
            )}
        </div>
    );
}
